import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { 
  Upload, 
  Download, 
  FileSpreadsheet, 
  Settings, 
  GripVertical,
  Check,
  X,
  AlertTriangle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Save,
  Eye,
  CheckCircle,
  XCircle,
  ArrowRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// Default columns matching backend import template
const DEFAULT_COLUMNS = [
  { key: 'admin_notes', label: 'Заметки', required: false, format: 'Заметки для планировщика' },
  { key: 'date', label: 'Дата', required: true, format: 'ГГГГ-ММ-ДД' },
  { key: 'time_start', label: 'Время', required: false, format: 'ЧЧ:ММ (10=10:00, 10.30=10:30)' },
  { key: 'route_name', label: 'Маршрут', required: false, format: 'Текст' },
  { key: 'description', label: 'Описание', required: false, format: 'Текст' },
  { key: 'start_name', label: 'Откуда', required: false, format: 'Текст' },
  { key: 'end_name', label: 'Куда', required: false, format: 'Текст' },
  { key: 'bus_number', label: 'Автобус', required: false, format: 'Госномер' },
  { key: 'driver_name', label: 'Водитель', required: false, format: 'Имя как в системе' },
  { key: 'payment_amount', label: 'Оплата €', required: false, format: 'Число' },
  { key: 'contact_phone', label: 'Телефон', required: false, format: 'Текст' },
  { key: 'is_international', label: 'Загран', required: false, format: '1=да, 0/пусто=нет' },
];

export default function TripImport({ onImportComplete }) {
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  // Load saved template
  useEffect(() => {
    loadTemplate();
  }, []);

  const loadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/api/import-template`, getAuthHeaders());
      if (response.data.columns && response.data.columns.length > 0) {
        setColumns(response.data.columns);
      }
    } catch (error) {
      console.error('Error loading template:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async () => {
    try {
      await axios.post(`${API}/api/import-template`, { columns }, getAuthHeaders());
      toast.success('Шаблон сохранён');
      setHasChanges(false);
    } catch (error) {
      toast.error('Ошибка сохранения шаблона');
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/api/import-template/download`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'trips_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Шаблон скачан');
    } catch (error) {
      toast.error('Ошибка скачивания шаблона');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Пожалуйста, загрузите Excel файл (.xlsx)');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/api/import-trips/preview`, formData, {
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setPreviewData(response.data);
      setPreviewDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка загрузки файла');
    } finally {
      setUploading(false);
      event.target.value = ''; // Reset input
    }
  };

  const executeImport = async () => {
    if (!previewData?.rows) return;

    setImporting(true);
    try {
      const response = await axios.post(`${API}/api/import-trips/execute`, {
        rows: previewData.rows
      }, getAuthHeaders());
      
      toast.success(response.data.message);
      setPreviewDialogOpen(false);
      setPreviewData(null);
      
      if (onImportComplete) {
        onImportComplete();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка импорта');
    } finally {
      setImporting(false);
    }
  };

  // Drag and drop handlers for column reordering
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newColumns = [...columns];
    const [removed] = newColumns.splice(draggedIndex, 1);
    newColumns.splice(index, 0, removed);
    setColumns(newColumns);
    setDraggedIndex(index);
    setHasChanges(true);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const moveColumn = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= columns.length) return;

    const newColumns = [...columns];
    [newColumns[index], newColumns[newIndex]] = [newColumns[newIndex], newColumns[index]];
    setColumns(newColumns);
    setHasChanges(true);
  };

  const resetToDefault = () => {
    setColumns(DEFAULT_COLUMNS);
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Main Import Card */}
      <Card className="border-dashed border-2 border-blue-200 bg-blue-50/50">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                <FileSpreadsheet className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            
            <div className="flex-1 text-center sm:text-left">
              <h3 className="font-semibold text-gray-800 mb-1">Импорт рейсов из Excel</h3>
              <p className="text-sm text-gray-500">
                Скачайте шаблон, заполните данные и загрузите обратно
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => setTemplateDialogOpen(true)}
                className="gap-2"
              >
                <Settings className="w-4 h-4" />
                Настроить шаблон
              </Button>
              
              <Button
                variant="outline"
                onClick={downloadTemplate}
                className="gap-2 border-green-300 text-green-700 hover:bg-green-50"
              >
                <Download className="w-4 h-4" />
                Скачать шаблон
              </Button>
              
              <label>
                <Button
                  className="gap-2 bg-blue-600 hover:bg-blue-700 cursor-pointer"
                  disabled={uploading}
                  asChild
                >
                  <span>
                    {uploading ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    Загрузить файл
                  </span>
                </Button>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Template Settings Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-600" />
              Настройка шаблона импорта
            </DialogTitle>
            <DialogDescription>
              Перетащите колонки для изменения порядка. Этот порядок будет использоваться в Excel-файле.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            <div className="space-y-2">
              {columns.map((col, index) => (
                <div
                  key={col.key}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-move transition-all ${
                    draggedIndex === index ? 'opacity-50 border-blue-400' : 'hover:border-gray-300'
                  } ${col.skip_import ? 'bg-gray-100' : 'bg-white'}`}
                >
                  <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium text-sm ${col.skip_import ? 'text-gray-400' : 'text-gray-800'}`}>
                      {col.label}
                    </div>
                    <div className="text-xs text-gray-400">{col.format}</div>
                  </div>
                  {col.required && (
                    <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">
                      Обязательно
                    </span>
                  )}
                  {col.skip_import && (
                    <span className="text-xs bg-gray-200 text-gray-500 px-2 py-0.5 rounded">
                      Не импортируется
                    </span>
                  )}
                  <div className="flex gap-1">
                    <button
                      onClick={() => moveColumn(index, -1)}
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => moveColumn(index, 1)}
                      disabled={index === columns.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex justify-between pt-4 border-t">
            <Button variant="outline" onClick={resetToDefault}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Сбросить
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>
                Отмена
              </Button>
              <Button onClick={saveTemplate} disabled={!hasChanges} className="bg-blue-600 hover:bg-blue-700">
                <Save className="w-4 h-4 mr-2" />
                Сохранить
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-600" />
              Предпросмотр импорта
            </DialogTitle>
            <DialogDescription>
              Проверьте данные перед импортом. Строки с ошибками не будут импортированы.
            </DialogDescription>
          </DialogHeader>
          
          {previewData && (
            <>
              {/* Stats */}
              <div className="flex gap-4 py-3 border-b">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <FileSpreadsheet className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-sm font-medium">{previewData.total}</div>
                    <div className="text-xs text-gray-500">всего строк</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-green-600">{previewData.valid_count}</div>
                    <div className="text-xs text-gray-500">готово к импорту</div>
                  </div>
                </div>
                {previewData.errors.length > 0 && (
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                      <XCircle className="w-4 h-4 text-red-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-red-600">{previewData.total - previewData.valid_count}</div>
                      <div className="text-xs text-gray-500">с ошибками</div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Table */}
              <div className="flex-1 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-2 text-left font-medium text-gray-600 w-10">#</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600 w-10">✓</th>
                      {previewData.columns.map(col => (
                        <th 
                          key={col.key} 
                          className={`px-2 py-2 text-left font-medium text-gray-600 whitespace-nowrap text-xs ${
                            col.skip_import ? 'bg-gray-200 text-gray-400' : ''
                          }`}
                        >
                          {col.label}
                          {col.skip_import && <span className="ml-1 text-[10px]">(—)</span>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {previewData.rows.map((row, idx) => (
                      <tr 
                        key={idx} 
                        className={`${
                          row._errors?.length > 0 
                            ? 'bg-red-50' 
                            : row._warnings?.length > 0 
                            ? 'bg-amber-50' 
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className="px-2 py-2 text-gray-500 text-xs">{row._row}</td>
                        <td className="px-2 py-2">
                          {row._errors?.length > 0 ? (
                            <XCircle className="w-4 h-4 text-red-500" />
                          ) : row._warnings?.length > 0 ? (
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                          ) : (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          )}
                        </td>
                        {previewData.columns.map(col => (
                          <td 
                            key={col.key} 
                            className={`px-2 py-2 max-w-[120px] truncate text-xs ${
                              col.skip_import ? 'bg-gray-100 text-gray-400' : ''
                            }`}
                            title={row[col.key] || ''}
                          >
                            {row[col.key] || '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Actions */}
              <div className="flex justify-between pt-4 border-t">
                <div className="text-sm text-gray-500">
                  {previewData.valid_count > 0 ? (
                    <>Будет импортировано <span className="font-medium text-green-600">{previewData.valid_count}</span> рейсов</>
                  ) : (
                    <span className="text-red-600">Нет валидных строк для импорта</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>
                    Отмена
                  </Button>
                  <Button 
                    onClick={executeImport} 
                    disabled={importing || previewData.valid_count === 0}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {importing ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ArrowRight className="w-4 h-4 mr-2" />
                    )}
                    Импортировать {previewData.valid_count} рейсов
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
